import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import { Visit } from '../types';
import PastVisitsService from '../../../api/services/PastVisitService';

const ACTIONS_PREFIX = 'Visit';

type PastVisitsProps = {
  visits: Visit[];
  isLoading: boolean;
  totalRowsFound: number;
  error?: string;
  query?: string;
};

const initialState: PastVisitsProps = {
  visits: [],
  isLoading: false,
  totalRowsFound: 0,
  query: '',
};

export const getPastVisitsThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getPastVisits`,
  async (
    params: Parameters<typeof PastVisitsService.getVisits>[0] & { clearPreviousData?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await PastVisitsService.getVisits(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred');
    }
  },
);

export const getVisitThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getVisit`,
  async (visitId: string, { rejectWithValue }) => {
    try {
      const response = await PastVisitsService.getVisit(visitId);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred');
    }
  },
);

const pastVisitsSlice = createSlice({
  name: 'pastVisits',
  initialState,
  reducers: {
    clearPastVisitsState: () => initialState,
    setQuery: (state, action) => {
      state.query = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPastVisitsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getPastVisitsThunk.fulfilled, (state, action) => {
        const currentVisits = current(state.visits).filter((visit) => visit !== undefined);
        let newVisits: Visit[] = [];

        if (action.payload && typeof action.payload === 'object') {
          const visits =
            action.payload.entry?.map((entry: any) => entry.resource).filter(Boolean) || [];
          newVisits = visits.filter(
            (visit: Visit) =>
              visit && visit.id && !currentVisits.find((m: Visit) => m && m.id === visit.id),
          );
          state.totalRowsFound = action.payload.total || 0;
        } else {
          console.error('Unexpected payload structure:', action.payload);
          state.totalRowsFound = 0;
        }

        if (action.meta.arg.clearPreviousData) {
          state.visits = newVisits;
        } else {
          state.visits =
            action.meta.arg.offset === 0
              ? [...newVisits, ...currentVisits]
              : [...currentVisits, ...newVisits];
        }

        state.isLoading = false;
      })
      .addCase(getPastVisitsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || action.error.message;
      })
      .addCase(getVisitThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getVisitThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getVisitThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || action.error.message;
      });
  },
});

export const selectPastVisitsState = (state: RootState) => state.pastVisits;

export const { clearPastVisitsState, setQuery } = pastVisitsSlice.actions;

export default pastVisitsSlice.reducer;
